import * as React from 'react';
import {FormattedMessage} from 'react-intl';

import {
    getCurrentLocale,
    getCurrentSite,
    getLocalizedKvm,
    getLocalizedPrice,
    getLocalizedRok
} from 'features/services/utils/env-helper';
import { HeadlineLevel, HeadlineSize, LOCALE, Margin, ParagraphSize, SITE } from 'features/ui/enums';
import {capitalizeFirstLetter} from 'features/services/utils/helpers';
import {renderInhabitDate} from 'features/services/utils/date-helpers';
import {DisposalForm, ResidenceType} from 'features/search/filters/enums';
import {cleanFinnishNames, getDisposalFormName, getMunicipalityName} from 'features/services/utils/name-helpers';

import './style.scss';

import {P} from 'components/atoms/Text/P';
import {Headline} from 'components/atoms/Text/Headline';
import {Icon} from 'components/atoms/Media/Icon';
import {SiteSpecificContent} from 'components/atoms/SiteSpecificContent';
import {MobileContentFold} from 'components/organisms/GeneralComponents/MobileContentFold';
import {getResidenceTypeIcon} from 'components/icons/icon-helper';
import {SearchResult} from "features/search/types";

interface Props {
    object: SearchResult;
    hideArrow?: boolean;
    isMobile?: boolean;
    collapsible?: boolean;
}

export const ObjectSuggestion: React.FunctionComponent<Props> = ({object, hideArrow, isMobile, collapsible}) => {
    const getResidenceTitle =() =>{
        if(getCurrentSite() === SITE.FI && typeof object.jmoyNumberOfRoomsExtraInfo !== 'undefined' && object.jmoyNumberOfRoomsExtraInfo !== null ) {
            return `${object.jmoyNumberOfRoomsExtraInfo}, ${getLocalizedKvm(object.primaryArea)}`
        }
        if (object.residenceType == ResidenceType.PLOT) {
            return `${getLocalizedKvm(object.plotArea)}`;
        }
        if (getCurrentSite() === SITE.NO && object.jmnoBraInternal) {
            return `${getLocalizedRok(object.rooms)}, ${getLocalizedKvm(object.jmnoBraInternal)}`;
        }
        return `${getLocalizedRok(object.rooms)}, ${getLocalizedKvm(object.primaryArea)}`;
    };

    const shouldRenderPrice = () => {
        if(!object.price || object.price === null || object.price === 0) return false;
        return true;
    };

    const renderFloor = () => {
        if(object.residenceType === ResidenceType.DETACHED_HOUSE ||
            object.residenceType === ResidenceType.HOUSE ||
            object.residenceType === ResidenceType.TOWN_HOUSE ||
            object.residenceType === ResidenceType.SEMI_DETACHED_HOUSE ||
            object.residenceType === ResidenceType.PLOT) return false;

        return true;
    };

    const getTotalPrice = () => {
        let price = object.price;

        if(object.otherExpenses) {
            price += object.otherExpenses;
        }

        if(object.jmnoFellesgjeld && object.residenceType === ResidenceType.APARTMENT && (object.disposalForm === DisposalForm.OWNERSHIP || object.disposalForm === DisposalForm.OWNERSHIP_APARTMENT)) {
            price += object.jmnoFellesgjeld;
        }

        return getLocalizedPrice(price);
    };

    return (
        <div className="object-suggestion">
            {((!isMobile && !collapsible) || (!isMobile && collapsible)) &&
                <React.Fragment>
                    {(process.env.NODE_ENV === 'development' && new Date().getMonth() === 5 && (new Date().getDate() > 14 && new Date().getDate() < 26)) ?
                        <P size={ParagraphSize.S}>Midsommarkransen</P> ://Easter egg
                        <P size={ParagraphSize.S} className="object-suggestion__breadcrumbs">{getMunicipalityName(object)} / {capitalizeFirstLetter(cleanFinnishNames(object.stageName))}</P>
                    }
                    <Headline 
                        level={HeadlineLevel.H2} 
                        size={HeadlineSize.M} 
                    >
                        {getResidenceTitle()}
                    </Headline>
                    <P margin={Margin.S}>
                        {object.disposalForm &&
                            <SiteSpecificContent notFinland>
                                {getDisposalFormName(object.disposalForm as DisposalForm)}
                            </SiteSpecificContent>
                        }
                        {/*divider*/
                            (object.disposalForm && shouldRenderPrice())
                            && <SiteSpecificContent notFinland>, </SiteSpecificContent>
                        }
                        {shouldRenderPrice() &&
                            <React.Fragment>
                                {object.jmoyTotalPrice && <SiteSpecificContent showOnFinland>Velaton hinta </SiteSpecificContent>}
                                {object.jmoyTotalPrice && <SiteSpecificContent showOnFinland><span>{getLocalizedPrice(object.jmoyTotalPrice)}</span></SiteSpecificContent>}
                                <SiteSpecificContent notFinland><span>{getLocalizedPrice(object.price)}</span></SiteSpecificContent>
                                <SiteSpecificContent showOnNorway>
                                    <React.Fragment>
                                        <br />
                                        Totalpris inkl. omk. {getTotalPrice()}
                                    </React.Fragment>
                                </SiteSpecificContent>
                            </React.Fragment>
                        }
                        {/*divider*/
                            ((object.disposalForm || shouldRenderPrice())
                            && renderFloor())
                            && <br />
                        }
                        {renderFloor() &&
                            <React.Fragment>
                                <FormattedMessage id="jm.components.ObjectSuggestion.floor" /> {object.floor} / {object.maxFloor} {getCurrentLocale() === LOCALE.FI && `, Asunto ${object.name && object.name.toUpperCase()}`}
                            </React.Fragment>
                        }
                        {/*divider*/
                            (
                                ((object.disposalForm || shouldRenderPrice())
                                || renderFloor())
                            && object.inhabitDate)
                            && <br />
                        }
                        {object.inhabitDate &&
                            <React.Fragment>
                                <FormattedMessage id="jm.components.ObjectSuggestion.inhabitDate" /> {renderInhabitDate(object.inhabitDate, object.inhabitDateEnd, object.inhabitDateDetermined)}
                            </React.Fragment>
                        }
                    </P>
                    <span className="residence-block__icon">
                        {getResidenceTypeIcon(object.residenceType)}
                    </span>
                </React.Fragment>
            }
            {(isMobile && collapsible) &&
                <MobileContentFold title={`${getLocalizedRok(object.rooms)}, ${getLocalizedKvm(object.primaryArea)}`}>
                    <P>{getMunicipalityName(object)} / {capitalizeFirstLetter(object.stageName)}</P>
                    <P><SiteSpecificContent showOnFinland>Velaton hinta</SiteSpecificContent> {getLocalizedPrice(object.price)}</P>
                    <P>
                        <FormattedMessage id="jm.components.ObjectSuggestion.floor" /> {object.floor} / {object.maxFloor} {getCurrentLocale() === LOCALE.FI && `, Asunto ${object.name && object.name.toUpperCase()}`}
                    </P>
                    <P>
                        <FormattedMessage id="jm.components.ObjectSuggestion.inhabitDate" /> {object.inhabitDate && renderInhabitDate(object.inhabitDate, object.inhabitDateEnd, object.inhabitDateDetermined)}
                    </P>
                    { !hideArrow &&
                        <Icon size={32} name="arrow-link" flip/>
                    }
                </MobileContentFold>
            }
        </div>
    )
};
